import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { MaterialLibrary, CreateMaterialLibrary, UpdateMaterialLibrary } from '@estimator/shared';

export function useMaterialsLibrary() {
  return useQuery({
    queryKey: ['materials-library'],
    queryFn: async () => {
      const { data } = await apiClient.get<MaterialLibrary[]>('/materials-library');
      return data;
    },
  });
}

export function useMaterialLibraryItem(id: string | null) {
  return useQuery({
    queryKey: ['materials-library', id],
    queryFn: async () => {
      const { data } = await apiClient.get<MaterialLibrary>(`/materials-library/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateMaterialLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateMaterialLibrary) => {
      const { data } = await apiClient.post<MaterialLibrary>('/materials-library', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['materials-library'] }),
  });
}

export function useUpdateMaterialLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateMaterialLibrary & { id: string }) => {
      const { data } = await apiClient.patch<MaterialLibrary>(`/materials-library/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['materials-library'] });
      queryClient.invalidateQueries({ queryKey: ['materials-library', vars.id] });
    },
  });
}

export function useDeleteMaterialLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/materials-library/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['materials-library'] }),
  });
}

export function useBulkDeleteMaterialLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await apiClient.post('/materials-library/bulk-delete', { ids });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['materials-library'] }),
  });
}
